import { ClientConfig } from '../types';

interface EntityColumn {
  type: string;
  displayName: string;
}

interface OrderedEntity {
  key: string;
  displayOrder: number;
  displayName: string;
  showInTable: boolean;
}

/**
 * Get all entities sorted by displayOrder.
 * Entities in config come first (by displayOrder), then any entities
 * found in account data but not in config are appended alphabetically.
 */
export function getOrderedEntities(
  entityDefinitions: ClientConfig['entityDefinitions'] | undefined,
  accountEntityKeys?: string[]
): OrderedEntity[] {
  const result: OrderedEntity[] = [];
  const seen = new Set<string>();

  // Add configured entities sorted by displayOrder
  if (entityDefinitions) {
    const configEntries = Object.entries(entityDefinitions)
      .map(([key, def]) => ({
        key,
        displayOrder: def.displayOrder ?? 999,
        displayName: def.displayName || def.name || key,
        showInTable: def.showInTable ?? true,
      }))
      .sort((a, b) => a.displayOrder - b.displayOrder);

    for (const entry of configEntries) {
      result.push(entry);
      seen.add(entry.key);
    }
  }

  // Append any account entities not in config (alphabetically, showInTable: false)
  if (accountEntityKeys) {
    const extras = accountEntityKeys
      .filter((key) => !seen.has(key))
      .sort();

    for (const key of extras) {
      result.push({
        key,
        displayOrder: 999,
        displayName: key.charAt(0).toUpperCase() + key.slice(1),
        showInTable: false,
      });
    }
  }

  return result;
}

/**
 * Get entity columns to show in the Accounts table.
 * Returns only entities with showInTable: true, in displayOrder.
 * Falls back to auto-detect (top 5 by frequency) when no config exists.
 */
export function getTableEntityColumns(
  entityDefinitions: ClientConfig['entityDefinitions'] | undefined,
  accounts?: Array<{ entities?: Record<string, any> }>
): EntityColumn[] {
  // If we have entity definitions, use config-driven columns
  if (entityDefinitions && Object.keys(entityDefinitions).length > 0) {
    return getOrderedEntities(entityDefinitions)
      .filter((e) => e.showInTable)
      .map((e) => ({ type: e.key, displayName: e.displayName }));
  }

  // Fallback: auto-detect top 5 entities by frequency
  if (!accounts) return [];

  const entityCounts = new Map<string, number>();
  accounts.forEach((account) => {
    if (account.entities) {
      Object.keys(account.entities).forEach((entityType) => {
        entityCounts.set(entityType, (entityCounts.get(entityType) || 0) + 1);
      });
    }
  });

  return Array.from(entityCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([type]) => ({
      type,
      displayName: type.charAt(0).toUpperCase() + type.slice(1) + 's',
    }));
}

/**
 * Get entity keys in configured order.
 * Useful for ordering iteration over account.entities.
 */
export function getOrderedEntityKeys(
  entityDefinitions: ClientConfig['entityDefinitions'] | undefined,
  accountEntityKeys: string[]
): string[] {
  return getOrderedEntities(entityDefinitions, accountEntityKeys).map((e) => e.key);
}
